import { signOut as fbSignOut } from 'firebase/auth'
import { getFirebaseAuth } from './firebase'

/**
 * What WeHunt Web does when it learns the natlink-sso session behind its
 * Firebase credential has ended somewhere else.
 *
 * The whole point of the reaction is that it is NOT a user-initiated sign-out.
 * It does not raise the signed-out marker and it does not go to sso's logout: it
 * signs out locally and reloads onto /sign-in, whose sso-mode page starts a login
 * that sso answers silently as whoever it holds NOW. That last hop is what turns
 * "sign out of the shop as A, sign in as B, come back" into arriving as B rather
 * than being stuck as A.
 */

/** The `code` the API puts in the body of the 401 it answers with. */
export const SESSION_ENDED_CODE = 'session_ended'

/** Where the browser goes to pick the current sso identity back up. */
const SIGN_IN_PATH = '/sign-in'

/**
 * When this tab last reacted to an ended session by leaving for `/sign-in`.
 *
 * The in-memory latch below cannot see across a page load, and the reaction ends
 * in one: if the local Firebase sign-out FAILS, the tab lands back on the app
 * still signed in, the next call 401s, the sign-out fails again, and it leaves
 * again -- a reload loop with no exit. A timestamp in `sessionStorage` survives
 * exactly the loads belonging to this tab's own loop, which is the scope of the
 * problem.
 */
export const SESSION_ENDED_AT_KEY = 'sso_session_ended_at'

/**
 * How soon after leaving a second departure is refused.
 *
 * Long enough to cover the sign-in page starting a login and sso answering it,
 * short enough that a session which genuinely ends twice in one sitting is not
 * left unreacted-to for long.
 */
export const RELOAD_CAP_MS = 10_000

function readLastEndedAt(): number | null {
  try {
    const raw = window.sessionStorage.getItem(SESSION_ENDED_AT_KEY)
    if (raw === null) return null
    const at = Number(raw)
    return Number.isFinite(at) ? at : null
  } catch {
    // A browser that denies storage costs us the loop cap, not the reaction.
    return null
  }
}

function markEndedAt(at: number): void {
  try {
    window.sessionStorage.setItem(SESSION_ENDED_AT_KEY, String(at))
  } catch {
  }
}

/**
 * Whether an error is the API saying the sso session is over.
 *
 * Duck-typed rather than an `instanceof ApiError` check: each web domain owns its
 * own `ApiError` class and core-web may not depend on any of them, so the shape
 * -- a numeric `status` and a `problem.code` -- is the only thing all three have
 * in common.
 */
export function isSessionEndedError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false
  const candidate = error as { status?: unknown; problem?: { code?: unknown } | null }
  if (candidate.status !== 401) return false
  return candidate.problem?.code === SESSION_ENDED_CODE
}

export interface EndSsoSessionLocallyDeps {
  signOut: () => Promise<void>
  /** Test seam. Production always navigates the real window. */
  navigate: (url: string) => void
}

function productionEndDeps(): EndSsoSessionLocallyDeps {
  return {
    signOut: () => fbSignOut(getFirebaseAuth()),
    // A full page load rather than a router navigation: the sign-in page decides
    // once per mount whether to start a login, and a fresh document is the only
    // way to be sure nothing from the dead session is still mounted and querying.
    navigate: (url) => { window.location.assign(url) },
  }
}

/**
 * Guarded for the page's whole lifetime, not merely while the sign-out is in
 * flight: every in-flight query fails at the same instant, and three navigations
 * to the same URL is three logins started. The flag is never cleared because the
 * navigation replaces the document, which discards this module with it.
 */
let ending = false

export async function endSsoSessionLocally(
  deps: EndSsoSessionLocallyDeps = productionEndDeps(),
): Promise<void> {
  if (ending) return
  ending = true

  try {
    await deps.signOut()
  } catch (err) {
    // Leaving matters more than leaving cleanly: the credential is already
    // rejected by the API, so staying only produces more 401s. Bounded by the
    // cap below, which is what keeps a sign-out that never succeeds from turning
    // "leave anyway" into an endless one.
    console.error('[ssoSession] the local sign-out failed while ending an sso session', err)
  }

  const lastEndedAt = readLastEndedAt()
  const now = Date.now()
  if (lastEndedAt !== null && now - lastEndedAt < RELOAD_CAP_MS) {
    // Only reachable when the previous departure did not take: a successful one
    // comes back signed in as whoever sso holds now and never gets here. The
    // local sign-out above has still run, so the tab is at least no longer acting
    // on a credential the server rejects.
    console.warn(
      '[ssoSession] the sso session ended again moments after the last reaction; not reloading',
    )
    return
  }
  markEndedAt(now)

  deps.navigate(SIGN_IN_PATH)
}

/**
 * The one 401 reaction, called by every domain's `apiFetch`.
 *
 * In `apiFetch` rather than in `useApiFetch` because most calls in the app do
 * not go through the hook: the clubhouse teams api modules, `apiFetchBlob` and
 * campfire's replay hook all call `apiFetch` directly.
 *
 * Returns whether it recognised the error, so a caller can tell the two kinds of
 * 401 apart. It never swallows: the caller still rethrows, because the query that
 * failed did fail.
 */
export function handleSessionEnded(
  error: unknown,
  deps?: EndSsoSessionLocallyDeps,
): boolean {
  if (!isSessionEndedError(error)) return false
  void endSsoSessionLocally(deps)
  return true
}
